import { useNavigate } from "react-router-dom";
import { Home as HomeIcon, LogOut } from "lucide-react";
import { LOGO_URL } from "@/config";
import formatCurrency from "@/utils/formatCurrency";
import AssetRow from "@/components/shared/AssetRow";
import SectionTotal from "@/components/shared/SectionTotal";

const NetWorthCalculator = ({ investmentAssets, liabilities, onUpdateInvestmentAssets, onUpdateLiabilities, user, onLogout }) => {
  const navigate = useNavigate();

  const totalDebtAssets = investmentAssets.debt_assets.reduce((sum, a) => sum + (a.amount || 0), 0);
  const totalEquityAssets = investmentAssets.equity_assets.reduce((sum, a) => sum + (a.amount || 0), 0);
  const totalGoldAssets = investmentAssets.gold_assets.reduce((sum, a) => sum + (a.amount || 0), 0);
  const totalRealEstateAssets = (investmentAssets.real_estate_assets || []).reduce((sum, a) => sum + (a.amount || 0), 0);
  const totalInvestmentAssets = totalDebtAssets + totalEquityAssets + totalGoldAssets + totalRealEstateAssets;
  const totalLiabilities = liabilities.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netWorth = totalInvestmentAssets - totalLiabilities;

  return (
    <div className="finance-app min-h-screen p-3 sm:p-6 lg:p-8" data-testid="networth-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-8 page-header-mobile">
          <div className="flex items-center gap-2 sm:gap-4">
            <img src={LOGO_URL} alt="SubiWealth" className="h-8 sm:h-12" />
            <h1 className="page-title">Net Worth Calculator</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 header-actions">
            <button className="nav-btn primary" onClick={() => navigate("/")}>
              <HomeIcon size={16} className="inline mr-1" /> Dashboard
            </button>
            <button className="p-2 text-gray-500 hover:text-red-500" onClick={onLogout}>
              <LogOut size={20} />
            </button>
          </div>
        </div>

        <div className="finance-card mb-6">
          <h2 className="section-title text-teal-600">Investment Assets</h2>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 italic">Debt Assets</h3>
            {investmentAssets.debt_assets.map((asset, index) => (
              <AssetRow key={asset.name} name={asset.name} amount={asset.amount} onChange={(val) => onUpdateInvestmentAssets('debt_assets', index, val)} />
            ))}
            <SectionTotal label="Total Debt Assets" amount={totalDebtAssets} colorClass="blue" />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 italic">Equity Assets</h3>
            {investmentAssets.equity_assets.map((asset, index) => (
              <AssetRow key={asset.name} name={asset.name} amount={asset.amount} onChange={(val) => onUpdateInvestmentAssets('equity_assets', index, val)} />
            ))}
            <SectionTotal label="Total Equity Assets" amount={totalEquityAssets} colorClass="green" />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 italic">Gold Assets</h3>
            {investmentAssets.gold_assets.map((asset, index) => (
              <AssetRow key={asset.name} name={asset.name} amount={asset.amount} onChange={(val) => onUpdateInvestmentAssets('gold_assets', index, val)} />
            ))}
            <SectionTotal label="Total Gold Assets" amount={totalGoldAssets} colorClass="yellow" />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 italic">Real Estate Assets</h3>
            {(investmentAssets.real_estate_assets || []).map((asset, index) => (
              <AssetRow key={asset.name} name={asset.name} amount={asset.amount} onChange={(val) => onUpdateInvestmentAssets('real_estate_assets', index, val)} />
            ))}
            <SectionTotal label="Total Real Estate Assets" amount={totalRealEstateAssets} colorClass="teal" />
          </div>

          <div className="mt-4 p-4 bg-teal-100 rounded-xl border-2 border-teal-300">
            <div className="flex items-center justify-between">
              <span className="text-xl font-bold text-teal-800">Total Investment Assets</span>
              <span className="text-2xl font-bold text-teal-600">₹{formatCurrency(totalInvestmentAssets)}</span>
            </div>
          </div>
        </div>

        <div className="finance-card mb-6">
          <h2 className="section-title text-red-600">Liabilities (Outstanding Loans)</h2>
          {liabilities.map((liability, index) => (
            <AssetRow key={liability.name} name={liability.name} amount={liability.amount} onChange={(val) => onUpdateLiabilities(index, val)} />
          ))}
          <div className="mt-4 p-4 bg-red-50 rounded-xl border-2 border-red-300">
            <div className="flex items-center justify-between">
              <span className="text-xl font-bold text-red-800">Total Liabilities</span>
              <span className="text-2xl font-bold text-red-600">₹{formatCurrency(totalLiabilities)}</span>
            </div>
          </div>
        </div>

        {/* My Net Worth - Prominent Summary */}
        <div className="finance-card" data-testid="networth-summary">
          <h2 className="section-title text-gray-800">My Net Worth</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-teal-50 rounded-xl border border-teal-200">
              <span className="text-lg font-semibold text-teal-700">Total Investment Assets</span>
              <span className="text-xl font-bold text-teal-600">₹{formatCurrency(totalInvestmentAssets)}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200">
              <span className="text-lg font-semibold text-red-700">Total Liabilities</span>
              <span className="text-xl font-bold text-red-600">₹{formatCurrency(totalLiabilities)}</span>
            </div>
            <div className={`p-6 rounded-xl border-2 ${netWorth >= 0 ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-xl font-bold ${netWorth >= 0 ? 'text-green-800' : 'text-red-800'}`}>Total Net Worth</span>
                  <p className="text-sm text-gray-500 mt-1">Total Investment Assets − Total Liabilities</p>
                </div>
                <span className={`text-3xl font-bold ${netWorth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₹{formatCurrency(netWorth)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetWorthCalculator;
